import React, { useState, useMemo } from 'react';

// Legacy status classifications matching Apps Script exactly
const completedStatuses = ['Closed', 'Completed', 'Posted', 'Resolved', 'Finished'];
const approvalStatuses = ['Pending Approval', 'Review', 'Approval', 'Waiting for Approval'];
const inProgressStatuses = ['In Progress', 'Paused', 'Allocated', 'Planned', 'Assigned'];
const pendingStatuses = ['Open', 'Rework', 'Reassigned', 'Re-work', 'Pending', 'Urgent'];
const ignoredStatuses = ['Cancelled', 'Rejected', 'Dropped', 'Void'];

const isClosedGroup = (s = '') => completedStatuses.includes(s) || approvalStatuses.includes(s);
const isInProgressGroup = (s = '') => ['In Progress', 'Paused', 'Allocated', 'Planned', 'Assigned', 'Re-work', 'Rework'].includes(s);
const isPendingGroup = (s = '') => !isClosedGroup(s) && !isInProgressGroup(s) && !ignoredStatuses.includes(s);

function parseDurationMins(str) {
  if (!str) return 0;
  if (typeof str === 'number') return str;
  const raw = String(str).toLowerCase().trim();
  if (!isNaN(raw)) return parseInt(raw, 10);
  if (raw.includes(':')) {
    const parts = raw.split(':');
    const hrs = parseInt(parts[0], 10) || 0;
    const mins = parseInt(parts[1], 10) || 0;
    return hrs * 60 + mins;
  }
  let mins = 0;
  const parts = raw.split(/\s+/);
  let matched = false;
  parts.forEach((p) => {
    const num = parseInt(p, 10);
    if (!isNaN(num)) {
      if (p.includes('h')) {
        mins += num * 60;
        matched = true;
      } else if (p.includes('m')) {
        mins += num;
        matched = true;
      }
    }
  });
  if (!matched && parts.length === 1) {
    const num = parseInt(raw, 10);
    if (!isNaN(num)) return num;
  }
  return mins;
}

function fmtMins(m) {
  if (isNaN(m) || m === null || m === undefined) return '0h 0m';
  const h = Math.floor(m / 60);
  const r = Math.round(m % 60);
  return `${h}h ${r}m`;
}

function sortTasksByStatusPriority(tasks = []) {
  const priority = (s) => {
    if (isPendingGroup(s)) return 1;
    if (isInProgressGroup(s)) return 2;
    if (approvalStatuses.includes(s)) return 3;
    if (completedStatuses.includes(s)) return 4;
    return 5;
  };
  return [...tasks].sort((a, b) => {
    const pDiff = priority(a.Status) - priority(b.Status);
    if (pDiff !== 0) return pDiff;
    return String(b.Date || '').localeCompare(String(a.Date || ''));
  });
}

function AllClientsSummaryTable({ stats = [], onSelectClient }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [sortField, setSortField] = useState('total');
  const [sortOrder, setSortOrder] = useState('desc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const grandTotals = useMemo(() => {
    let totalTickets = 0;
    let pendingTickets = 0;
    let completeTickets = 0;
    let reworkTickets = 0;
    stats.forEach((c) => {
      totalTickets += Number(c.total || 0);
      pendingTickets += Number(c.pending || 0);
      completeTickets += Number(c.completed || 0);
      reworkTickets += Number(c.rework || 0);
    });
    return {
      totalClients: stats.length,
      totalTickets,
      pendingTickets,
      completeTickets,
      reworkTickets,
      closureRate: totalTickets ? Math.round((completeTickets / totalTickets) * 100) : 0
    };
  }, [stats]);

  const filteredStats = useMemo(() => {
    let result = [...stats];
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter(
        (c) =>
          String(c.clientName || '').toLowerCase().includes(q) ||
          String(c.clientId || '').toLowerCase().includes(q)
      );
    }

    if (statusFilter === 'HAS_PENDING') {
      result = result.filter((c) => Number(c.pending || 0) > 0);
    } else if (statusFilter === 'HAS_REWORK') {
      result = result.filter((c) => Number(c.rework || 0) > 0);
    } else if (statusFilter === 'COMPLETED_100') {
      result = result.filter((c) => Number(c.total || 0) > 0 && c.completed === c.total);
    }

    result.sort((a, b) => {
      const valA = a[sortField];
      const valB = b[sortField];
      if (typeof valA === 'string') {
        const cmp = String(valA || '').localeCompare(String(valB || ''));
        return sortOrder === 'asc' ? cmp : -cmp;
      }
      return sortOrder === 'asc'
        ? (Number(valA) || 0) - (Number(valB) || 0)
        : (Number(valB) || 0) - (Number(valA) || 0);
    });

    return result;
  }, [stats, searchQuery, statusFilter, sortField, sortOrder]);

  const filteredTotals = useMemo(() => {
    let totalTickets = 0;
    let pendingTickets = 0;
    let completeTickets = 0;
    let reworkTickets = 0;
    filteredStats.forEach((c) => {
      totalTickets += Number(c.total || 0);
      pendingTickets += Number(c.pending || 0);
      completeTickets += Number(c.completed || 0);
      reworkTickets += Number(c.rework || 0);
    });
    return {
      totalClients: filteredStats.length,
      totalTickets,
      pendingTickets,
      completeTickets,
      reworkTickets,
      closureRate: totalTickets ? Math.round((completeTickets / totalTickets) * 100) : 0
    };
  }, [filteredStats]);

  const countPending = useMemo(() => stats.filter((c) => Number(c.pending || 0) > 0).length, [stats]);
  const countRework = useMemo(() => stats.filter((c) => Number(c.rework || 0) > 0).length, [stats]);
  const countCompleted = useMemo(
    () => stats.filter((c) => Number(c.total || 0) > 0 && c.completed === c.total).length,
    [stats]
  );

  const renderSortIndicator = (field) => {
    if (sortField !== field) {
      return <span style={{ color: '#cbd5e1', marginLeft: '4px', fontSize: '10px' }}>⇅</span>;
    }
    return (
      <span style={{ color: '#4f46e5', marginLeft: '4px', fontSize: '11px', fontWeight: '900' }}>
        {sortOrder === 'asc' ? '▲' : '▼'}
      </span>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* 5 KPI Summary Cards for All Clients */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
        {/* Total Clients */}
        <div
          style={{
            background: '#fff',
            padding: '16px 20px',
            borderRadius: '14px',
            border: '1px solid #e2e8f0',
            borderBottom: '4px solid #6366f1',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
          }}
        >
          <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Active Clients
          </div>
          <div style={{ fontSize: '24px', fontWeight: '800', color: '#1e293b', marginTop: '6px' }}>
            {grandTotals.totalClients}
          </div>
          <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>Total client accounts</div>
        </div>

        {/* Total Tickets */}
        <div
          style={{
            background: '#fff',
            padding: '16px 20px',
            borderRadius: '14px',
            border: '1px solid #e2e8f0',
            borderBottom: '4px solid #3b82f6',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
          }}
        >
          <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Total Tickets
          </div>
          <div style={{ fontSize: '24px', fontWeight: '800', color: '#2563eb', marginTop: '6px' }}>
            {grandTotals.totalTickets.toLocaleString()}
          </div>
          <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>Across all clients</div>
        </div>

        {/* Pending Tickets */}
        <div
          style={{
            background: '#fff',
            padding: '16px 20px',
            borderRadius: '14px',
            border: '1px solid #e2e8f0',
            borderBottom: '4px solid #f59e0b',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
          }}
        >
          <div style={{ fontSize: '11px', fontWeight: '700', color: '#b45309', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Pending Tickets
          </div>
          <div style={{ fontSize: '24px', fontWeight: '800', color: '#d97706', marginTop: '6px' }}>
            {grandTotals.pendingTickets.toLocaleString()}
          </div>
          <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>Open & In Progress</div>
        </div>

        {/* Complete Tickets */}
        <div
          style={{
            background: '#fff',
            padding: '16px 20px',
            borderRadius: '14px',
            border: '1px solid #e2e8f0',
            borderBottom: '4px solid #10b981',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
          }}
        >
          <div style={{ fontSize: '11px', fontWeight: '700', color: '#047857', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Complete Tickets
          </div>
          <div style={{ fontSize: '24px', fontWeight: '800', color: '#059669', marginTop: '6px' }}>
            {grandTotals.completeTickets.toLocaleString()}
          </div>
          <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>Closed & Resolved</div>
        </div>

        {/* Rework Tickets */}
        <div
          style={{
            background: '#fff',
            padding: '16px 20px',
            borderRadius: '14px',
            border: '1px solid #e2e8f0',
            borderBottom: '4px solid #8b5cf6',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
          }}
        >
          <div style={{ fontSize: '11px', fontWeight: '700', color: '#6d28d9', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Rework Tickets
          </div>
          <div style={{ fontSize: '24px', fontWeight: '800', color: '#7c3aed', marginTop: '6px' }}>
            {grandTotals.reworkTickets.toLocaleString()}
          </div>
          <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>Quality revision tickets</div>
        </div>
      </div>

      {/* Search & Filter Toolbar */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '14px',
          background: '#fff',
          padding: '14px 20px',
          borderRadius: '14px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
        }}
      >
        {/* Search */}
        <div style={{ position: 'relative', minWidth: '280px', flex: '1 1 280px' }}>
          <input
            type="text"
            id="cx-client-table-search"
            placeholder="Search by client name or ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '9px 34px 9px 36px',
              borderRadius: '10px',
              border: '1px solid #cbd5e1',
              fontSize: '13px',
              outline: 'none',
              background: '#f8fafc',
              boxSizing: 'border-box'
            }}
          />
          <svg
            style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', width: '16px', height: '16px', color: '#94a3b8' }}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                color: '#94a3b8',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              ✕
            </button>
          )}
        </div>

        {/* Filter Pills */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
          <button
            type="button"
            onClick={() => setStatusFilter('ALL')}
            style={{
              padding: '6px 14px',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: '700',
              cursor: 'pointer',
              border: '1px solid',
              borderColor: statusFilter === 'ALL' ? '#6366f1' : '#e2e8f0',
              background: statusFilter === 'ALL' ? '#eef2ff' : '#fff',
              color: statusFilter === 'ALL' ? '#4338ca' : '#64748b',
              transition: 'all 0.15s ease'
            }}
          >
            All Clients ({stats.length})
          </button>

          <button
            type="button"
            onClick={() => setStatusFilter('HAS_PENDING')}
            style={{
              padding: '6px 14px',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: '700',
              cursor: 'pointer',
              border: '1px solid',
              borderColor: statusFilter === 'HAS_PENDING' ? '#f59e0b' : '#e2e8f0',
              background: statusFilter === 'HAS_PENDING' ? '#fef3c7' : '#fff',
              color: statusFilter === 'HAS_PENDING' ? '#b45309' : '#64748b',
              transition: 'all 0.15s ease'
            }}
          >
            Has Pending ({countPending})
          </button>

          <button
            type="button"
            onClick={() => setStatusFilter('HAS_REWORK')}
            style={{
              padding: '6px 14px',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: '700',
              cursor: 'pointer',
              border: '1px solid',
              borderColor: statusFilter === 'HAS_REWORK' ? '#8b5cf6' : '#e2e8f0',
              background: statusFilter === 'HAS_REWORK' ? '#ede9fe' : '#fff',
              color: statusFilter === 'HAS_REWORK' ? '#6d28d9' : '#64748b',
              transition: 'all 0.15s ease'
            }}
          >
            Has Rework ({countRework})
          </button>

          <button
            type="button"
            onClick={() => setStatusFilter('COMPLETED_100')}
            style={{
              padding: '6px 14px',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: '700',
              cursor: 'pointer',
              border: '1px solid',
              borderColor: statusFilter === 'COMPLETED_100' ? '#10b981' : '#e2e8f0',
              background: statusFilter === 'COMPLETED_100' ? '#d1fae5' : '#fff',
              color: statusFilter === 'COMPLETED_100' ? '#047857' : '#64748b',
              transition: 'all 0.15s ease'
            }}
          >
            100% Completed ({countCompleted})
          </button>
        </div>
      </div>

      {/* Table Container */}
      <div
        style={{
          background: '#fff',
          borderRadius: '16px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
          overflow: 'hidden'
        }}
      >
        <div style={{ overflowX: 'auto', maxHeight: '680px' }}>
          <table
            id="cx-all-clients-table"
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              textAlign: 'left',
              fontSize: '13px'
            }}
          >
            <thead
              style={{
                position: 'sticky',
                top: 0,
                background: '#f8fafc',
                borderBottom: '2px solid #e2e8f0',
                zIndex: 5
              }}
            >
              <tr>
                <th style={{ padding: '14px 16px', fontWeight: '800', color: '#475569', width: '50px', textAlign: 'center' }}>#</th>
                <th
                  onClick={() => handleSort('clientId')}
                  style={{ padding: '14px 16px', fontWeight: '800', color: '#475569', cursor: 'pointer', userSelect: 'none', width: '120px' }}
                >
                  Client ID {renderSortIndicator('clientId')}
                </th>
                <th
                  onClick={() => handleSort('clientName')}
                  style={{ padding: '14px 16px', fontWeight: '800', color: '#475569', cursor: 'pointer', userSelect: 'none' }}
                >
                  Client Name {renderSortIndicator('clientName')}
                </th>
                <th
                  onClick={() => handleSort('total')}
                  style={{ padding: '14px 16px', fontWeight: '800', color: '#475569', textAlign: 'right', cursor: 'pointer', userSelect: 'none', width: '130px' }}
                >
                  Total Tickets {renderSortIndicator('total')}
                </th>
                <th
                  onClick={() => handleSort('pending')}
                  style={{ padding: '14px 16px', fontWeight: '800', color: '#b45309', textAlign: 'right', cursor: 'pointer', userSelect: 'none', width: '140px' }}
                >
                  Pending Tickets {renderSortIndicator('pending')}
                </th>
                <th
                  onClick={() => handleSort('completed')}
                  style={{ padding: '14px 16px', fontWeight: '800', color: '#047857', textAlign: 'right', cursor: 'pointer', userSelect: 'none', width: '140px' }}
                >
                  Complete Tickets {renderSortIndicator('completed')}
                </th>
                <th
                  onClick={() => handleSort('rework')}
                  style={{ padding: '14px 16px', fontWeight: '800', color: '#6d28d9', textAlign: 'right', cursor: 'pointer', userSelect: 'none', width: '120px' }}
                >
                  Rework {renderSortIndicator('rework')}
                </th>
                <th
                  onClick={() => handleSort('closureRate')}
                  style={{ padding: '14px 16px', fontWeight: '800', color: '#475569', textAlign: 'center', cursor: 'pointer', userSelect: 'none', width: '140px' }}
                >
                  Closure Rate {renderSortIndicator('closureRate')}
                </th>
                <th style={{ padding: '14px 16px', fontWeight: '800', color: '#475569', textAlign: 'center', width: '140px' }}>
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredStats.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ padding: '48px 16px', textAlign: 'center', color: '#94a3b8' }}>
                    <div style={{ fontSize: '15px', fontWeight: '700', color: '#64748b' }}>No clients found</div>
                    <div style={{ fontSize: '12px', marginTop: '4px' }}>Try adjusting your search query or filter</div>
                  </td>
                </tr>
              ) : (
                filteredStats.map((c, index) => {
                  return (
                    <tr
                      key={c.clientId || index}
                      onClick={() => onSelectClient(c.clientId)}
                      style={{
                        borderBottom: '1px solid #f1f5f9',
                        transition: 'background-color 0.15s ease',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f8fafc')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      <td style={{ padding: '13px 16px', textAlign: 'center', color: '#94a3b8', fontWeight: '600', fontSize: '12px' }}>
                        {index + 1}
                      </td>
                      <td style={{ padding: '13px 16px' }}>
                        <span
                          style={{
                            fontFamily: 'monospace',
                            fontSize: '12px',
                            fontWeight: '700',
                            background: '#e0e7ff',
                            color: '#4338ca',
                            padding: '3px 8px',
                            borderRadius: '6px'
                          }}
                        >
                          {c.clientId}
                        </span>
                      </td>
                      <td style={{ padding: '13px 16px' }}>
                        <div style={{ fontWeight: '700', color: '#0f172a', fontSize: '13.5px' }}>
                          {c.clientName}
                        </div>
                      </td>
                      <td style={{ padding: '13px 16px', textAlign: 'right' }}>
                        <span
                          style={{
                            display: 'inline-block',
                            fontWeight: '800',
                            color: '#1e293b',
                            background: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            padding: '4px 10px',
                            borderRadius: '8px',
                            minWidth: '45px',
                            textAlign: 'center'
                          }}
                        >
                          {Number(c.total || 0).toLocaleString()}
                        </span>
                      </td>
                      <td style={{ padding: '13px 16px', textAlign: 'right' }}>
                        <span
                          style={{
                            display: 'inline-block',
                            fontWeight: '800',
                            color: Number(c.pending || 0) > 0 ? '#b45309' : '#64748b',
                            background: Number(c.pending || 0) > 0 ? '#fef3c7' : '#f8fafc',
                            border: `1px solid ${Number(c.pending || 0) > 0 ? '#fde68a' : '#e2e8f0'}`,
                            padding: '4px 10px',
                            borderRadius: '8px',
                            minWidth: '45px',
                            textAlign: 'center'
                          }}
                        >
                          {Number(c.pending || 0).toLocaleString()}
                        </span>
                      </td>
                      <td style={{ padding: '13px 16px', textAlign: 'right' }}>
                        <span
                          style={{
                            display: 'inline-block',
                            fontWeight: '800',
                            color: '#047857',
                            background: '#d1fae5',
                            border: '1px solid #a7f3d0',
                            padding: '4px 10px',
                            borderRadius: '8px',
                            minWidth: '45px',
                            textAlign: 'center'
                          }}
                        >
                          {Number(c.completed || 0).toLocaleString()}
                        </span>
                      </td>
                      <td style={{ padding: '13px 16px', textAlign: 'right' }}>
                        <span
                          style={{
                            display: 'inline-block',
                            fontWeight: '800',
                            color: Number(c.rework || 0) > 0 ? '#6d28d9' : '#94a3b8',
                            background: Number(c.rework || 0) > 0 ? '#ede9fe' : '#f8fafc',
                            border: `1px solid ${Number(c.rework || 0) > 0 ? '#ddd6fe' : '#e2e8f0'}`,
                            padding: '4px 10px',
                            borderRadius: '8px',
                            minWidth: '40px',
                            textAlign: 'center'
                          }}
                        >
                          {Number(c.rework || 0).toLocaleString()}
                        </span>
                      </td>
                      <td style={{ padding: '13px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                          <div style={{ width: '56px', height: '6px', background: '#e2e8f0', borderRadius: '3px', overflow: 'hidden' }}>
                            <div
                              style={{
                                width: `${c.closureRate || 0}%`,
                                height: '100%',
                                background: (c.closureRate || 0) >= 70 ? '#10b981' : (c.closureRate || 0) >= 40 ? '#f59e0b' : '#ef4444',
                                borderRadius: '3px'
                              }}
                            />
                          </div>
                          <span
                            style={{
                              fontSize: '12px',
                              fontWeight: '800',
                              color: (c.closureRate || 0) >= 70 ? '#047857' : (c.closureRate || 0) >= 40 ? '#b45309' : '#b91c1c',
                              minWidth: '34px',
                              textAlign: 'right'
                            }}
                          >
                            {c.closureRate || 0}%
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: '13px 16px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={() => onSelectClient(c.clientId)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '6px 12px',
                            borderRadius: '8px',
                            border: '1px solid #cbd5e1',
                            background: '#fff',
                            color: '#4338ca',
                            fontSize: '12px',
                            fontWeight: '700',
                            cursor: 'pointer',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                            transition: 'all 0.15s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#4338ca';
                            e.currentTarget.style.color = '#fff';
                            e.currentTarget.style.borderColor = '#4338ca';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#fff';
                            e.currentTarget.style.color = '#4338ca';
                            e.currentTarget.style.borderColor = '#cbd5e1';
                          }}
                        >
                          View Details ➔
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            {/* Sticky Totals Footer Row */}
            <tfoot
              style={{
                position: 'sticky',
                bottom: 0,
                background: '#f8fafc',
                borderTop: '2px solid #cbd5e1',
                zIndex: 4,
                boxShadow: '0 -2px 4px rgba(0,0,0,0.03)'
              }}
            >
              <tr style={{ fontWeight: '800' }}>
                <td style={{ padding: '14px 16px', textAlign: 'center', color: '#64748b' }}>∑</td>
                <td style={{ padding: '14px 16px', color: '#4338ca', fontFamily: 'monospace' }}>TOTAL</td>
                <td style={{ padding: '14px 16px', color: '#1e293b' }}>
                  All Clients Summary ({filteredTotals.totalClients})
                </td>
                <td style={{ padding: '14px 16px', textAlign: 'right', color: '#1e293b' }}>
                  <span style={{ background: '#e2e8f0', padding: '4px 10px', borderRadius: '8px' }}>
                    {filteredTotals.totalTickets.toLocaleString()}
                  </span>
                </td>
                <td style={{ padding: '14px 16px', textAlign: 'right', color: '#b45309' }}>
                  <span style={{ background: '#fef3c7', border: '1px solid #fde68a', padding: '4px 10px', borderRadius: '8px' }}>
                    {filteredTotals.pendingTickets.toLocaleString()}
                  </span>
                </td>
                <td style={{ padding: '14px 16px', textAlign: 'right', color: '#047857' }}>
                  <span style={{ background: '#d1fae5', border: '1px solid #a7f3d0', padding: '4px 10px', borderRadius: '8px' }}>
                    {filteredTotals.completeTickets.toLocaleString()}
                  </span>
                </td>
                <td style={{ padding: '14px 16px', textAlign: 'right', color: '#6d28d9' }}>
                  <span style={{ background: '#ede9fe', border: '1px solid #ddd6fe', padding: '4px 10px', borderRadius: '8px' }}>
                    {filteredTotals.reworkTickets.toLocaleString()}
                  </span>
                </td>
                <td style={{ padding: '14px 16px', textAlign: 'center', color: '#1e293b' }}>
                  <span style={{ fontSize: '13px', fontWeight: '800' }}>{filteredTotals.closureRate}% Avg</span>
                </td>
                <td style={{ padding: '14px 16px', textAlign: 'center', color: '#94a3b8' }}>—</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}

export function ClientExplorerSection({
  clients = [],
  selectedClientId = '',
  onClientChange,
  selectedClient = null,
  explorer = {},
  tickets = [],
  fms = [],
  todo = [],
  invoices = [],
  clientTicketStats = [],
  currentUser = null
}) {
  // Collapsible cards state
  const [openInvoices, setOpenInvoices] = useState(true);
  const [openWorkItems, setOpenWorkItems] = useState(true);
  const [openBandwidth, setOpenBandwidth] = useState(true);

  // Active client object
  const currentClient = useMemo(() => {
    if (!selectedClientId) return null;
    return (
      selectedClient ||
      clients.find(
        (c) =>
          String(c.Client_Id || c.id || '').trim().toUpperCase() ===
          String(selectedClientId || '').trim().toUpperCase()
      ) ||
      null
    );
  }, [clients, selectedClientId, selectedClient]);

  const clientName = currentClient ? (currentClient['Client Name'] || currentClient.name || '') : '';
  const clientId = currentClient ? (currentClient.Client_Id || currentClient.id || '') : '';

  const userRole = String(currentUser?.Role || currentUser?.role || '').trim();
  const isSuperAdminOrManager = /super\s*admin|admin|manager/i.test(userRole);

  const effectiveClientStats = useMemo(() => {
    if (Array.isArray(clientTicketStats) && clientTicketStats.length > 0) {
      return clientTicketStats;
    }
    if (Array.isArray(explorer?.clientTicketStats) && explorer.clientTicketStats.length > 0) {
      return explorer.clientTicketStats;
    }
    const statsMap = new Map();
    tickets.forEach((t) => {
      const cid = String(t.Client_Id || t['Client ID'] || '').trim().toLowerCase();
      const cname = String(t.Client || t['Client Name'] || '').trim().toLowerCase();
      const status = String(t.Status || '').trim().toLowerCase();
      const isClosed = /closed|completed|done|resolved/i.test(status);
      const isRework = /rework|reassigned|re-work/i.test(status);

      const key = cid || cname;
      if (!key) return;
      const cur = statsMap.get(key) || { total: 0, completed: 0, rework: 0 };
      cur.total += 1;
      if (isClosed) cur.completed += 1;
      if (isRework) cur.rework += 1;
      statsMap.set(key, cur);
      if (cid && cname) statsMap.set(cname, cur);
    });

    return clients
      .map((c) => {
        const cid = String(c.Client_Id || c.id || '').trim();
        const cname = String(c['Client Name'] || c.name || cid).trim();
        const found = statsMap.get(cid.toLowerCase()) || statsMap.get(cname.toLowerCase()) || { total: 0, completed: 0, rework: 0 };
        const pending = Math.max(0, found.total - found.completed);
        return {
          clientId: cid,
          clientName: cname,
          total: found.total,
          pending,
          completed: found.completed,
          rework: found.rework,
          closureRate: found.total ? Math.round((found.completed / found.total) * 100) : 0
        };
      })
      .sort((a, b) => b.total - a.total);
  }, [clientTicketStats, explorer, tickets, clients]);

  // Filter tasks for this client (tickets, fms, todo)
  const clientTasks = useMemo(() => {
    if (!currentClient) return [];
    const targetName = clientName.toLowerCase().trim();
    const targetId = clientId.toLowerCase().trim();

    const matchesClient = (item = {}) => {
      const c = String(item.Client || item.ClientName || item['Client Name'] || '').toLowerCase().trim();
      const cId = String(item.Client_Id || item.ClientID || item.clientId || '').toLowerCase().trim();
      if (targetId && (cId === targetId || c === targetId)) return true;
      if (targetName && (c === targetName || c.includes(targetName) || (targetName.length > 3 && targetName.includes(c)))) return true;
      return false;
    };

    const tFiltered = tickets.filter(matchesClient).map((t) => ({ ...t, Type: 'Ticket' }));
    const fFiltered = fms.filter(matchesClient).map((t) => ({ ...t, Type: 'FMS' }));
    const toFiltered = todo.filter(matchesClient).map((t) => ({ ...t, Type: 'Todo' }));

    return [...tFiltered, ...fFiltered, ...toFiltered];
  }, [currentClient, clientName, clientId, tickets, fms, todo]);

  // Filter invoices for this client
  const clientInvoices = useMemo(() => {
    if (!currentClient) return [];
    const targetName = clientName.toLowerCase().trim();
    const targetId = clientId.toLowerCase().trim();

    return invoices.filter((inv) => {
      const cId = String(inv.Client_Id || inv.ClientID || inv.id || '').toLowerCase().trim();
      const cName = String(inv['Client Name'] || inv.Client || inv.ClientName || '').toLowerCase().trim();
      if (targetId && (cId === targetId || cName === targetId)) return true;
      if (targetName && (cName === targetName || cName.includes(targetName) || (targetName.length > 3 && targetName.includes(cName)))) return true;
      return false;
    });
  }, [currentClient, clientName, clientId, invoices]);

  // KPI Calculations matching Apps Script lines 3042-3085
  const {
    totalCount,
    pendingCount,
    progressCount,
    closedCount,
    totalPlanMins,
    pendingPlanMins,
    progressPlanMins,
    closedLoggedMins
  } = useMemo(() => {
    let tot = 0;
    let pend = 0;
    let prog = 0;
    let clos = 0;

    let totPlan = 0;
    let pendPlan = 0;
    let progPlan = 0;
    let closAct = 0;

    clientTasks.forEach((t) => {
      const s = t.Status || '';
      if (ignoredStatuses.includes(s)) return;

      tot++;
      const pMins = parseInt(t.TAT, 10) || 0;
      const aMins = parseDurationMins(t.Duration);
      totPlan += pMins;

      if (isClosedGroup(s)) {
        clos++;
        closAct += aMins;
      } else if (isInProgressGroup(s)) {
        prog++;
        progPlan += pMins;
      } else {
        pend++;
        pendPlan += pMins;
      }
    });

    return {
      totalCount: tot,
      pendingCount: pend,
      progressCount: prog,
      closedCount: clos,
      totalPlanMins: totPlan,
      pendingPlanMins: pendPlan,
      progressPlanMins: progPlan,
      closedLoggedMins: closAct
    };
  }, [clientTasks]);

  // Invoice breakdown summary
  const { totalInvoices, pendingInvoices, clearedInvoices, outstandingTotal } = useMemo(() => {
    const totalInv = clientInvoices.length;
    let pendInv = 0;
    let clrInv = 0;
    let totalOut = 0;

    clientInvoices.forEach((i) => {
      const outAmt = Number(i.Outstanding || 0);
      totalOut += outAmt;
      if (outAmt > 0) {
        pendInv++;
      } else {
        clrInv++;
      }
    });

    return {
      totalInvoices: totalInv,
      pendingInvoices: pendInv,
      clearedInvoices: clrInv,
      outstandingTotal: totalOut
    };
  }, [clientInvoices]);

  // Bandwidth by employee matching Apps Script lines 3086-3137
  const bandwidthData = useMemo(() => {
    const empMap = {};
    let totalAct = 0;
    let totalPlan = 0;

    clientTasks.forEach((t) => {
      if (ignoredStatuses.includes(t.Status)) return;

      const empName = t.User || 'Unassigned';
      if (!empMap[empName]) {
        empMap[empName] = {
          name: empName,
          count: 0,
          plan: 0,
          act: 0
        };
      }

      empMap[empName].count++;
      const planMins = parseInt(t.TAT, 10) || 0;
      empMap[empName].plan += planMins;
      totalPlan += planMins;

      const actMins = parseDurationMins(t.Duration);
      empMap[empName].act += actMins;
      totalAct += actMins;
    });

    const sorted = Object.values(empMap).sort((a, b) => b.act - a.act);
    return {
      employees: sorted,
      totalActMins: totalAct,
      totalPlanMins: totalPlan
    };
  }, [clientTasks]);

  const sortedTasks = useMemo(() => sortTasksByStatusPriority(clientTasks), [clientTasks]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* ==================== PREMIUM COMPACT SELECTION BAR WITH DUAL-ROW RULES ==================== */}
      <div
        style={{
          background: '#fff',
          padding: '20px',
          borderRadius: '16px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}
      >
        {/* Top Row: Title on Left, Dropdown strictly on the Right */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                background: '#f3e8ff',
                color: '#9333ea',
                padding: '10px',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ width: 22, height: 22, minWidth: 22, minHeight: 22 }}
              >
                <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div>
              <h2 style={{ fontSize: '16px', fontWeight: '800', color: '#1e293b', margin: 0 }}>Client Explorer</h2>
              <p style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', margin: '4px 0 0 0' }}>
                Consolidated workload, tickets & billing details
              </p>
            </div>
          </div>

          <select
            id="cx-client-selector"
            value={selectedClientId}
            onChange={(e) => onClientChange(e.target.value)}
            style={{
              background: '#f8fafc',
              border: '1px solid #cbd5e1',
              borderRadius: '10px',
              padding: '10px 16px',
              fontSize: '13px',
              fontWeight: '700',
              color: '#334155',
              outline: 'none',
              minWidth: '240px',
              cursor: 'pointer'
            }}
          >
            {isSuperAdminOrManager ? (
              <option value="">📁 All Clients Overview (Summary Table)</option>
            ) : (
              <option value="">Select a Client...</option>
            )}
            {clients.map((c) => (
              <option key={c.Client_Id || c.id} value={c.Client_Id || c.id}>
                {c['Client Name'] || c.name} ({c.Client_Id || c.id})
              </option>
            ))}
          </select>
        </div>

        {/* Bottom Row: Explicit Rules Strip matching Apps Script lines 1251-1277 */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '12px',
            fontSize: '11px',
            fontWeight: '700',
            fontFamily: 'monospace',
            color: '#64748b',
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            padding: '8px 14px',
            borderRadius: '10px'
          }}
        >
          <span style={{ color: '#94a3b8', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
              <circle cx="12" cy="12" r="10" />
              <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
            </svg>
            Rules:
          </span>

          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10b981' }} />
            <span style={{ color: '#1e293b' }}>Closed</span>
            <span style={{ color: '#94a3b8', fontWeight: '500' }}>(= Closed + Pending Approval)</span>
          </span>

          <span style={{ color: '#cbd5e1' }}>|</span>

          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#f59e0b' }} />
            <span style={{ color: '#1e293b' }}>In Progress</span>
            <span style={{ color: '#94a3b8', fontWeight: '500' }}>(= In Progress + Paused)</span>
          </span>

          <span style={{ color: '#cbd5e1' }}>|</span>

          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#f43f5e' }} />
            <span style={{ color: '#1e293b' }}>Pending</span>
            <span style={{ color: '#94a3b8', fontWeight: '500' }}>(= Open + Rework + Reassigned)</span>
          </span>
        </div>
      </div>

      {/* ==================== CONTENT STATE (ALL CLIENTS TABLE OR SINGLE CLIENT DRILLDOWN) ==================== */}
      {!currentClient ? (
        isSuperAdminOrManager ? (
          <AllClientsSummaryTable
            stats={effectiveClientStats}
            onSelectClient={onClientChange}
          />
        ) : (
          <div
            style={{
              background: '#fff',
              borderRadius: '16px',
              border: '1px dashed #cbd5e1',
              padding: '56px 20px',
              textAlign: 'center'
            }}
          >
            <div style={{ color: '#94a3b8', marginBottom: '14px' }}>
              <svg
                width="44"
                height="44"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ margin: '0 auto', width: 44, height: 44 }}
              >
                <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <p style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', margin: 0 }}>
              Select a Client from the dropdown list to audit their work items, outstanding balances, and open projects.
            </p>
          </div>
        )
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Back to All Clients Summary Table button for Super Admin / Manager */}
          {isSuperAdminOrManager && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '12px',
                background: '#fff',
                padding: '12px 18px',
                borderRadius: '14px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>
                  Auditing Client:
                </span>
                <span style={{ fontSize: '15px', fontWeight: '800', color: '#1e293b' }}>
                  {clientName}
                </span>
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: '700',
                    fontFamily: 'monospace',
                    background: '#e0e7ff',
                    color: '#4338ca',
                    padding: '3px 8px',
                    borderRadius: '6px'
                  }}
                >
                  {clientId}
                </span>
              </div>
              <button
                type="button"
                id="cx-back-to-all-clients-btn"
                onClick={() => onClientChange('')}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '7px 16px',
                  borderRadius: '10px',
                  border: '1px solid #cbd5e1',
                  background: '#fff',
                  fontSize: '12px',
                  fontWeight: '700',
                  color: '#4338ca',
                  cursor: 'pointer',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#eef2ff';
                  e.currentTarget.style.borderColor = '#c7d2fe';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#fff';
                  e.currentTarget.style.borderColor = '#cbd5e1';
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                ← Back to All Clients Summary Table
              </button>
            </div>
          )}
          {/* ==================== 4 CLIENT KPI SUMMARY CARDS ==================== */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '16px' }}>
            {/* Card 1: Total Tickets */}
            <div
              style={{
                background: '#fff',
                padding: '18px 20px',
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                borderBottom: '4px solid #6366f1',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <div>
                <span style={{ fontSize: '11px', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Total Tickets Raised
                </span>
                <div style={{ fontSize: '28px', fontWeight: '900', color: '#1e293b', marginTop: '4px' }} id="cx-kpi-total">
                  {totalCount}
                </div>
              </div>
              <div style={{ background: '#e0e7ff', color: '#6366f1', padding: '12px', borderRadius: '50%' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 22, height: 22 }}>
                  <path d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                </svg>
              </div>
            </div>

            {/* Card 2: Active Pending */}
            <div
              style={{
                background: '#fff',
                padding: '18px 20px',
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                borderBottom: '4px solid #f59e0b',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <div>
                <span style={{ fontSize: '11px', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Active Pending Work
                </span>
                <div style={{ fontSize: '28px', fontWeight: '900', color: '#f59e0b', marginTop: '4px' }} id="cx-kpi-pending">
                  {pendingCount}
                </div>
              </div>
              <div style={{ background: '#fef3c7', color: '#d97706', padding: '12px', borderRadius: '50%' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 22, height: 22 }}>
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
              </div>
            </div>

            {/* Card 3: Time Allocation */}
            <div
              style={{
                background: '#fff',
                padding: '18px 20px',
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                borderBottom: '4px solid #3b82f6',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <div>
                <span style={{ fontSize: '11px', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Time Allocation
                </span>
                <div style={{ fontSize: '28px', fontWeight: '900', color: '#2563eb', marginTop: '4px' }} id="cx-kpi-time">
                  {fmtMins(totalPlanMins)}
                </div>
              </div>
              <div style={{ background: '#dbeafe', color: '#2563eb', padding: '12px', borderRadius: '50%' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 22, height: 22 }}>
                  <path d="M21.21 15.89A10 10 0 118 2.83" />
                  <path d="M22 12A10 10 0 0012 2v10z" />
                </svg>
              </div>
            </div>

            {/* Card 4: Outstanding Dues */}
            <div
              style={{
                background: '#fff',
                padding: '18px 20px',
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                borderBottom: '4px solid #10b981',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <div>
                <span style={{ fontSize: '11px', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Outstanding Dues
                </span>
                <div style={{ fontSize: '28px', fontWeight: '900', color: '#059669', marginTop: '4px' }} id="cx-kpi-bills">
                  ₹{outstandingTotal.toLocaleString('en-IN')}
                </div>
              </div>
              <div style={{ background: '#d1fae5', color: '#059669', padding: '12px', borderRadius: '50%' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 22, height: 22 }}>
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <line x1="12" y1="8" x2="12" y2="16" />
                  <line x1="8" y1="12" x2="16" y2="12" />
                </svg>
              </div>
            </div>
          </div>

          {/* ==================== CARD 1: INVOICE BREAKDOWN ==================== */}
          <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
            <div
              onClick={() => setOpenInvoices(!openInvoices)}
              style={{
                padding: '16px 20px',
                borderBottom: '1px solid #f1f5f9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                background: '#fff'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ background: '#d1fae5', color: '#059669', padding: '8px', borderRadius: '10px' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}>
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                  </svg>
                </div>
                <h3 style={{ fontSize: '15px', fontWeight: '800', color: '#1e293b', margin: 0 }}>Invoice Breakdown</h3>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px', fontWeight: '700', fontFamily: 'monospace' }}>
                  <span style={{ color: '#2563eb' }}>Total: {totalInvoices}</span>
                  <span style={{ color: '#cbd5e1' }}>|</span>
                  <span style={{ color: '#e11d48' }}>Pending: {pendingInvoices}</span>
                  <span style={{ color: '#cbd5e1' }}>|</span>
                  <span style={{ color: '#059669' }}>Cleared: {clearedInvoices}</span>
                </div>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{
                    width: 16,
                    height: 16,
                    transform: openInvoices ? 'rotate(0deg)' : 'rotate(-90deg)',
                    transition: 'transform 0.2s',
                    color: '#94a3b8'
                  }}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>
            </div>

            {openInvoices && (
              <div style={{ padding: '20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '14px' }}>
                  {clientInvoices.length === 0 ? (
                    <div style={{ padding: '24px', textAlign: 'center', fontSize: '13px', color: '#94a3b8', fontWeight: '600', background: '#f8fafc', borderRadius: '12px', border: '1px dashed #cbd5e1', gridColumn: '1 / -1' }}>
                      No ledger balances recorded for this client.
                    </div>
                  ) : (
                    clientInvoices.map((inv, idx) => {
                      const outAmt = Number(inv.Outstanding || 0);
                      const isOverdue = outAmt > 0;
                      return (
                        <div
                          key={inv.InvoiceID || inv._id || idx}
                          style={{
                            padding: '16px',
                            background: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                        >
                          <div>
                            <span style={{ fontSize: '13px', fontWeight: '800', color: '#1e293b', display: 'block' }}>
                              {inv.Invoice_No || inv.InvoiceID || 'General Invoice'}
                            </span>
                            <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '600', display: 'block', marginTop: '4px' }}>
                              Due: {inv.DueDateFmt || inv.DueDate || inv.Date || '-'} | Collector: {inv.AssignedCollector || inv.Collector || 'Internal'}
                            </span>
                          </div>
                          <span
                            style={{
                              padding: '6px 10px',
                              borderRadius: '8px',
                              fontFamily: 'monospace',
                              fontWeight: '800',
                              fontSize: '13px',
                              background: isOverdue ? '#fff1f2' : '#ecfdf5',
                              color: isOverdue ? '#e11d48' : '#059669',
                              border: isOverdue ? '1px solid #fecdd3' : '1px solid #a7f3d0'
                            }}
                          >
                            ₹{outAmt.toLocaleString('en-IN')}
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ==================== CARD 2: CLIENT SUPPORT WORK ITEMS ==================== */}
          <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
            <div
              onClick={() => setOpenWorkItems(!openWorkItems)}
              style={{
                padding: '16px 20px',
                borderBottom: '1px solid #f1f5f9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                background: '#fff'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ background: '#f3e8ff', color: '#9333ea', padding: '8px', borderRadius: '10px' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}>
                    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                  </svg>
                </div>
                <h3 style={{ fontSize: '15px', fontWeight: '800', color: '#1e293b', margin: 0 }}>Client Support Work Items</h3>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px', fontWeight: '700', fontFamily: 'monospace' }}>
                  <span style={{ color: '#2563eb' }}>Total: {fmtMins(totalPlanMins)} ({totalCount})</span>
                  <span style={{ color: '#cbd5e1' }}>|</span>
                  <span style={{ color: '#e11d48' }}>Pending: {fmtMins(pendingPlanMins)} ({pendingCount})</span>
                  <span style={{ color: '#cbd5e1' }}>|</span>
                  <span style={{ color: '#d97706' }}>In Progress: {fmtMins(progressPlanMins)} ({progressCount})</span>
                  <span style={{ color: '#cbd5e1' }}>|</span>
                  <span style={{ color: '#059669' }}>Closed: {fmtMins(closedLoggedMins)} ({closedCount})</span>
                </div>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{
                    width: 16,
                    height: 16,
                    transform: openWorkItems ? 'rotate(0deg)' : 'rotate(-90deg)',
                    transition: 'transform 0.2s',
                    color: '#94a3b8'
                  }}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>
            </div>

            {openWorkItems && (
              <div style={{ padding: '16px 20px', overflowX: 'auto', maxHeight: '500px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ color: '#64748b', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                      <th style={{ padding: '10px 12px', textAlign: 'left' }}>Date</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left' }}>Type</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left' }}>Description</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left' }}>Engineer</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', width: '100px' }}>Plan TAT</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', width: '100px' }}>Actual TAT</th>
                      <th style={{ padding: '10px 16px', textAlign: 'right' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedTasks.length === 0 ? (
                      <tr>
                        <td colSpan="7" style={{ textAlign: 'center', padding: '30px', color: '#94a3b8' }}>
                          All work clear! No support records pending.
                        </td>
                      </tr>
                    ) : (
                      sortedTasks.map((t, idx) => {
                        const isFms = t.Type === 'FMS';
                        const isTodo = t.Type === 'Todo' || t.Type === 'To-Do';

                        let badgeBg = '#dbeafe';
                        let badgeColor = '#1d4ed8';
                        let badgeLabel = 'TKT';

                        if (isFms) {
                          badgeBg = '#ccfbf1';
                          badgeColor = '#0f766e';
                          badgeLabel = 'FMS';
                        } else if (isTodo) {
                          badgeBg = '#e0e7ff';
                          badgeColor = '#4338ca';
                          badgeLabel = 'TODO';
                        }

                        let statusBg = '#f1f5f9';
                        let statusColor = '#475569';
                        if (isClosedGroup(t.Status)) {
                          statusBg = '#ecfdf5';
                          statusColor = '#059669';
                        } else if (approvalStatuses.includes(t.Status)) {
                          statusBg = '#f3e8ff';
                          statusColor = '#9333ea';
                        } else if (isInProgressGroup(t.Status)) {
                          statusBg = '#fef3c7';
                          statusColor = '#d97706';
                        } else if (isPendingGroup(t.Status)) {
                          statusBg = '#fff1f2';
                          statusColor = '#e11d48';
                        }

                        const actualFormatted = t.Duration || '0h 0m';
                        const planMins = parseInt(t.TAT, 10) || 0;
                        const actualMins = parseDurationMins(actualFormatted);

                        let timeWarnStyle = {};
                        if (!isFms && planMins > 0) {
                          if (actualMins > planMins) {
                            timeWarnStyle = { color: '#e11d48', background: '#fff1f2', padding: '2px 6px', borderRadius: '6px', fontWeight: '800' };
                          } else if (actualMins > 0) {
                            timeWarnStyle = { color: '#059669', background: '#ecfdf5', padding: '2px 6px', borderRadius: '6px', fontWeight: '800' };
                          }
                        }

                        return (
                          <tr key={t.ID || t._id || idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '10px 12px', color: '#64748b', fontSize: '11px', whiteSpace: 'nowrap' }}>
                              {t.Date || '-'}
                            </td>
                            <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                              <span style={{ background: badgeBg, color: badgeColor, padding: '3px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: '800' }}>
                                {badgeLabel}
                              </span>
                            </td>
                            <td style={{ padding: '10px 12px', fontWeight: '600', color: '#1e293b', maxWidth: '300px' }} title={t.Desc || t.Description || '-'}>
                              {t.Desc || t.Description || '-'}
                            </td>
                            <td style={{ padding: '10px 12px', fontWeight: '700', color: '#475569', whiteSpace: 'nowrap' }}>
                              {t.User || 'Unassigned'}
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'center', fontFamily: 'monospace', fontWeight: '700', color: '#2563eb', whiteSpace: 'nowrap' }}>
                              {fmtMins(planMins)}
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'center', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                              <span style={timeWarnStyle}>{actualFormatted}</span>
                            </td>
                            <td style={{ padding: '10px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                              <span style={{ background: statusBg, color: statusColor, padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '800' }}>
                                {t.Status || 'Open'}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ==================== CARD 3: EMPLOYEE BANDWIDTH & RESOURCE DISTRIBUTION ==================== */}
          <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
            <div
              onClick={() => setOpenBandwidth(!openBandwidth)}
              style={{
                padding: '16px 20px',
                borderBottom: '1px solid #f1f5f9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                background: '#fff'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ background: '#ccfbf1', color: '#0f766e', padding: '8px', borderRadius: '10px' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}>
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                </div>
                <h3 style={{ fontSize: '15px', fontWeight: '800', color: '#1e293b', margin: 0 }}>
                  Employee Bandwidth & Resource Distribution
                </h3>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px', fontWeight: '700', fontFamily: 'monospace' }}>
                  <span style={{ color: '#2563eb' }}>Employees: {bandwidthData.employees.length}</span>
                  <span style={{ color: '#cbd5e1' }}>|</span>
                  <span style={{ color: '#d97706' }}>Planned: {fmtMins(bandwidthData.totalPlanMins)}</span>
                  <span style={{ color: '#cbd5e1' }}>|</span>
                  <span style={{ color: '#059669' }}>Logged: {fmtMins(bandwidthData.totalActMins)}</span>
                </div>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{
                    width: 16,
                    height: 16,
                    transform: openBandwidth ? 'rotate(0deg)' : 'rotate(-90deg)',
                    transition: 'transform 0.2s',
                    color: '#94a3b8'
                  }}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>
            </div>

            {openBandwidth && (
              <div style={{ padding: '16px 20px', overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ color: '#64748b', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                      <th style={{ padding: '10px 12px', textAlign: 'left' }}>Employee Name</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center' }}>Assigned Tasks</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center' }}>Total Planned TAT</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center' }}>Total Actual Logged</th>
                      <th style={{ padding: '10px 16px', textAlign: 'right' }}>Time Share (%)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bandwidthData.employees.length === 0 ? (
                      <tr>
                        <td colSpan="5" style={{ textAlign: 'center', padding: '30px', color: '#94a3b8' }}>
                          No active resource logs recorded for this client.
                        </td>
                      </tr>
                    ) : (
                      bandwidthData.employees.map((e, idx) => {
                        const sharePct =
                          bandwidthData.totalActMins > 0
                            ? Math.round((e.act / bandwidthData.totalActMins) * 100)
                            : 0;

                        let progressColor = '#94a3b8';
                        if (sharePct > 50) progressColor = '#9333ea';
                        else if (sharePct > 25) progressColor = '#2563eb';
                        else if (sharePct > 10) progressColor = '#0d9488';

                        return (
                          <tr key={e.name || idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '12px', fontWeight: '800', color: '#1e293b', whiteSpace: 'nowrap' }}>
                              {e.name}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center', fontWeight: '700', color: '#334155' }}>
                              {e.count}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center', fontFamily: 'monospace', fontWeight: '700', color: '#94a3b8' }}>
                              {fmtMins(e.plan)}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center', fontFamily: 'monospace', fontWeight: '700', color: '#1e293b' }}>
                              {fmtMins(e.act)}
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end' }}>
                                <span style={{ fontFamily: 'monospace', fontWeight: '800', color: '#475569', fontSize: '12px' }}>
                                  {sharePct}%
                                </span>
                                <div style={{ width: '80px', height: '6px', background: '#f1f5f9', borderRadius: '999px', overflow: 'hidden' }}>
                                  <div style={{ width: `${Math.min(sharePct, 100)}%`, height: '100%', background: progressColor, borderRadius: '999px' }} />
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
